import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Mouse from 'core/mouse';
import { clamp } from 'helpers/math';
import styles from './popover.scss';
import Measure from 'react-measure';
import { Fade } from '../transition';

type PopoverOrigin = ['right' | 'center' | 'left', 'top' | 'center' | 'bottom'];
const MOVE_THRESHOLD = 100;
type Props = {
  z?: Number,
  open: Boolean,
  moveThreshold?: number,
  onRequestClose: () => void,
  noAnimation: boolean,
  className: String,
  /** Used to ignore the checkMouseInside function, causes problems with selectField */
  ignorePopover: Boolean,
  contentStyle: React.CSSProperties,
  children: React.CElement,
  /** Either anchorEl or position needs top be defined */
  position?: [Number, Number],
  anchorEl: HTMLElement,
  /** anchor can be specified instead of anchor el (this is the postion of the popover) */
  anchor?: { top: Number, left: Number },
  anchorOrigin: PopoverOrigin,
  targetOrigin: PopoverOrigin
};
type State = {};

const ANCHOR_OFFSET = {
  top: 0,
  left: 0,
  center: 0.5,
  bottom: 1,
  right: 1
};

export default class Popover extends Component {
  props: Props;
  state: State;
  el: HTMLDivElement;
  lastPos: Array;
  frameReq;

  constructor(props) {
    super(props);
    this.el = document.createElement('div');
    this.el.classList.add(styles.root);
  }

  componentDidMount() {
    document.body.appendChild(this.el);
    Mouse.on('mouseup', this.checkMouseInside, 100);
    if (this.props.open) {
      this.updatePos();
      this.startCheckMovement();
    }
  }

  componentDidUpdate() {
    if (this.props.open) {
      this.updatePos();
      this.startCheckMovement();
    }
  }

  componentWillUnmount() {
    Mouse.off('mouseup', this.checkMouseInside);
    this.el.remove();
  }

  startCheckMovement() {
    this.lastPos = false;
    if (!this.frameReq) this.checkMovement();
  }

  checkMovement = () => {
    const { open, anchorEl, onRequestClose } = this.props;
    if (open && anchorEl && onRequestClose) {
      const { top, left } = anchorEl.getBoundingClientRect();
      const dx = Math.abs(this.lastPos[0] - top);
      const dy = Math.abs(this.lastPos[1] - left);
      const moveThreshold = this.props.moveThreshold || MOVE_THRESHOLD;

      if (dx > moveThreshold || dy > moveThreshold) {
        onRequestClose();
        this.frameReq = false;
      } else if (dx || dy) {
        this.updatePos();
      }

      this.frameReq = requestAnimationFrame(this.checkMovement);
    } else {
      this.frameReq = false;
    }
  };

  updatePos(
    {
      anchorEl,
      position,
      anchor,
      offsetY = 0,
      offsetX = 0,
      z = 0,
      anchorOrigin = ['top', 'left'],
      targetOrigin = ['top', 'left']
    } = this.props
  ) {
    let top = 0,
      left = 0;

    if (position) {
      [left, top] = position;
    } else if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const [anchorX, anchorY] = anchorOrigin;
      top = rect.top + offsetY + rect.height * (ANCHOR_OFFSET[anchorY] || 0);
      left = rect.left + offsetX + rect.width * (ANCHOR_OFFSET[anchorX] || 0);

      if (!this.lastPos) this.lastPos = [rect.top, rect.left];
    } else if (anchor) {
      top = anchor.top;
      left = anchor.left;
    }

    if (this.bounds) {
      const targetX = ANCHOR_OFFSET[targetOrigin[0]] || 0;
      const targetY = ANCHOR_OFFSET[targetOrigin[1]] || 0;

      left -= this.bounds.width * targetX;
      top -= this.bounds.height * targetY;
      top = clamp(top, 0, window.innerHeight - this.bounds.height);
      left = clamp(left, 0, window.innerWidth - this.bounds.width);
    }

    this.el.style.top = top + 'px';
    this.el.style.left = left + 'px';
    if (z) this.el.style.zIndex = 101 + z;
  }

  checkMouseInside = (e: MouseEvent) => {
    const { open, onRequestClose, ignorePopover } = this.props;
    if (
      onRequestClose &&
      open &&
      !Mouse.isInside(e, this.el) &&
      (ignorePopover || !Mouse.isInsideRole(e, 'popover'))
    ) {
      onRequestClose(e);
    }
  };

  handleRect = ({ bounds }) => {
    this.bounds = bounds;
    this.updatePos(this.props);
  };

  renderPopover() {
    const { open, noAnimation, children, className, contentStyle } = this.props;
    let cl = styles.content;
    if (className) cl += ' ' + className;

    return (
      <Measure bounds onResize={this.handleRect}>
        {({ measureRef }) => (
          <div ref={measureRef} role="popover" className="fill" onWheel={e => e.stopPropagation()}>
            <Fade in={!!open} duration={noAnimation ? 0 : 300} className={cl} style={contentStyle}>
              {children}
            </Fade>
          </div>
        )}
      </Measure>
    );
  }

  render() {
    return ReactDOM.createPortal(this.renderPopover(), this.el);
  }
}
